import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import {
  Bot,
  ChevronUp,
  Megaphone,
  Play,
  Receipt,
  RotateCcw,
  TerminalSquare,
} from "lucide-react";
import { MockAd, MockChat, MockLedger } from "./AutomationStages";
import { cn } from "@/lib/utils";

const stages = [
  { icon: Megaphone, label: "Anúncio" },
  { icon: Bot, label: "Chatbot IA" },
  { icon: Receipt, label: "PDV & Caixa" },
];

const finalLogs = [
  "anúncio veiculado · campanha 'Ração Premium'",
  "clique detectado · lead: Mariana S.",
  "chatbot IA iniciou atendimento",
  "lead qualificado · interesse: plano mensal",
  "enviando venda ao Abilly PDV...",
  "venda registrada · R$ 189,90 · fluxo de caixa atualizado ✓",
];

/** Linha de log com efeito typewriter */
const LogLine = ({ text, active }: { text: string; active: boolean }) => {
  const [shown, setShown] = useState(0);

  useEffect(() => {
    if (shown >= text.length) return;
    const t = setTimeout(() => setShown((s) => s + 1), 14);
    return () => clearTimeout(t);
  }, [shown, text]);

  return (
    <p className="whitespace-pre-wrap break-words">
      <span className="mr-2 text-ring/50">›</span>
      {text.slice(0, shown)}
      {active && <span className="ml-0.5 animate-pulse text-ring">▊</span>}
    </p>
  );
};

const AutomationShowcase = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const inView = useInView(sectionRef, { margin: "-120px" });
  const reducedMotion = useReducedMotion();

  const [open, setOpen] = useState(false);
  const [runId, setRunId] = useState(0);
  const [step, setStep] = useState(0); // 0 idle · 1 anúncio · 2 chat · 3/4 registro
  const [chatCount, setChatCount] = useState(0);
  const [logs, setLogs] = useState<string[]>([]);

  useEffect(() => {
    if (reducedMotion) {
      setStep(4);
      setChatCount(4);
      setLogs(finalLogs);
      return;
    }
    if (!inView || !open) return;

    // Reinicia o estado a cada loop
    setStep(0);
    setChatCount(0);
    setLogs([]);

    const pushLog = (line: string) => setLogs((prev) => [...prev, line]);

    const timeline: Array<[number, () => void]> = [
      [600, () => { setStep(1); pushLog(finalLogs[0]); }],
      [2400, () => pushLog(finalLogs[1])],
      [3400, () => { setStep(2); pushLog(finalLogs[2]); }],
      [4300, () => setChatCount(1)],
      [5800, () => setChatCount(2)],
      [7300, () => setChatCount(3)],
      [8700, () => { setChatCount(4); pushLog(finalLogs[3]); }],
      [9900, () => { setStep(3); pushLog(finalLogs[4]); }],
      [11300, () => { setStep(4); pushLog(finalLogs[5]); }],
      [15500, () => setRunId((r) => r + 1)], // loop
    ];

    const timers = timeline.map(([at, fn]) => setTimeout(fn, at));
    return () => timers.forEach(clearTimeout);
  }, [runId, inView, reducedMotion, open]);

  const progress = [0, 16, 50, 82, 100][step];
  const typing = step === 2 && chatCount < 4 && chatCount % 2 === 1;

  return (
    <section className="bg-background py-20" ref={sectionRef}>
      <div className="container mx-auto px-4">
        {/* Teaser — sempre visível, abre a demonstração */}
        <AnimatePresence initial={false}>
          {!open && (
            <motion.button
              key="teaser"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35 }}
              onClick={() => setOpen(true)}
              className="group mx-auto flex w-full max-w-3xl items-center gap-5 rounded-2xl border-2 border-dashed border-primary/30 bg-secondary/50 px-6 py-6 text-left transition-all duration-300 hover:border-primary/60 hover:bg-secondary hover:shadow-lg"
            >
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary transition-transform duration-300 group-hover:scale-110">
                <Play size={22} className="ml-0.5" />
              </span>
              <span>
                <span className="block text-xs font-medium uppercase tracking-widest text-primary">
                  Automação em ação
                </span>
                <span className="mt-1 block font-heading text-lg font-bold text-foreground md:text-xl">
                  Veja a IA trabalhando ao vivo
                </span>
                <span className="mt-0.5 block text-sm text-muted-foreground">
                  Do anúncio ao caixa, sem toque humano — clique para abrir a demonstração interativa.
                </span>
              </span>
            </motion.button>
          )}
        </AnimatePresence>

        {/* Conteúdo expansível */}
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="content"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div className="mx-auto mb-14 max-w-3xl space-y-4 pt-4 text-center">
                <span className="inline-block text-sm font-medium uppercase tracking-widest text-primary">
                  Automação em ação
                </span>
                <h2 className="font-heading text-4xl font-bold text-foreground text-balance md:text-5xl">
                  Do anúncio ao caixa,{" "}
                  <span className="bg-hero-gradient bg-clip-text text-transparent">sem toque humano</span>
                </h2>
                <div className="mx-auto h-1 w-20 rounded-full bg-hero-gradient" />
                <p className="text-lg text-muted-foreground">
                  Veja como nossas automações com IA capturam o lead, atendem e registram a venda — tudo sozinho.
                </p>
              </div>

              {/* Janela do terminal */}
              <div className="mx-auto max-w-5xl overflow-hidden rounded-2xl border border-ring/20 bg-[hsl(var(--terminal))] shadow-2xl">
                {/* Moldura da janela */}
                <div className="flex items-center gap-2 border-b border-ring/10 px-5 py-3">
                  <span className="h-3 w-3 rounded-full bg-primary/50" />
                  <span className="h-3 w-3 rounded-full bg-ring/60" />
                  <span className="h-3 w-3 rounded-full bg-primary/25" />
                  <span className="ml-3 flex items-center gap-2 text-xs font-medium text-[hsl(var(--terminal-foreground))]/60">
                    <TerminalSquare size={14} className="text-ring/70" />
                    abillyting ~ automação
                  </span>
                  <button
                    onClick={() => setRunId((r) => r + 1)}
                    aria-label="Reiniciar animação"
                    className="ml-auto flex items-center gap-1.5 rounded-md border border-ring/20 px-2.5 py-1 text-[11px] font-medium text-[hsl(var(--terminal-foreground))]/70 transition-colors hover:border-ring/50 hover:text-ring"
                  >
                    <RotateCcw size={12} />
                    Replay
                  </button>
                </div>

                {/* Barra de progresso das etapas */}
                <div className="border-b border-ring/10 px-6 py-5">
                  <div className="relative flex items-center justify-between">
                    {/* Trilha */}
                    <div className="absolute left-0 right-0 top-1/2 h-0.5 -translate-y-1/2 bg-ring/10" />
                    <motion.div
                      className="absolute left-0 top-1/2 h-0.5 -translate-y-1/2 bg-ring"
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.8, ease: "easeInOut" }}
                    />
                    {stages.map((stage, i) => {
                      const Icon = stage.icon;
                      const activeStep = step === 0 ? -1 : step >= 4 ? 2 : step - 1;
                      const isActive = i <= activeStep;
                      const isCurrent = i === activeStep;
                      return (
                        <div key={stage.label} className="relative z-10 flex flex-col items-center gap-2">
                          <motion.span
                            animate={isCurrent ? { scale: [1, 1.12, 1] } : { scale: 1 }}
                            transition={isCurrent ? { duration: 1.4, repeat: Infinity } : {}}
                            className={cn(
                              "flex h-11 w-11 items-center justify-center rounded-full border-2 transition-colors duration-500",
                              isActive
                                ? "border-ring bg-primary text-primary-foreground"
                                : "border-ring/20 bg-[hsl(var(--terminal-card))] text-[hsl(var(--terminal-foreground))]/40"
                            )}
                          >
                            <Icon size={18} />
                          </motion.span>
                          <span
                            className={cn(
                              "text-[10px] font-semibold uppercase tracking-wide transition-colors duration-500 sm:text-xs",
                              isActive ? "text-ring" : "text-[hsl(var(--terminal-foreground))]/40"
                            )}
                          >
                            {stage.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Área principal: palco + log */}
                <div className="grid grid-cols-1 lg:grid-cols-5">
                  {/* Palco da animação */}
                  <div className="relative h-[380px] border-b border-ring/10 lg:col-span-3 lg:h-[420px] lg:border-b-0 lg:border-r">
                    <AnimatePresence mode="wait">
                      {step <= 1 && (
                        <motion.div key="ad" className="h-full" exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
                          <MockAd clicked={step >= 1} />
                        </motion.div>
                      )}
                      {step === 2 && (
                        <motion.div key="chat" className="h-full" exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
                          <MockChat visibleCount={chatCount} typing={typing} />
                        </motion.div>
                      )}
                      {step >= 3 && (
                        <motion.div key="ledger" className="h-full" exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
                          <MockLedger />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Log do terminal */}
                  <div className="h-[240px] overflow-hidden p-5 font-mono text-xs leading-relaxed text-[hsl(var(--terminal-foreground))]/80 lg:col-span-2 lg:h-auto">
                    <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-ring/60">
                      log de integração
                    </p>
                    <div className="space-y-2.5">
                      {logs.map((line, i) => (
                        <LogLine key={`${runId}-${i}`} text={line} active={i === logs.length - 1 && step < 4} />
                      ))}
                      {logs.length === 0 && (
                        <p className="text-[hsl(var(--terminal-foreground))]/40">
                          <span className="mr-2 text-ring/50">›</span>aguardando eventos...
                          <span className="ml-0.5 animate-pulse text-ring">▊</span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Botão recolher */}
              <div className="mt-8 text-center">
                <button
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2 text-sm font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
                >
                  <ChevronUp size={16} />
                  Recolher demonstração
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default AutomationShowcase;
